import './EngagementTables.scss';
import React, { useMemo, useState } from 'react';
import { FlightTable, FlightTextInput, FlightTooltip, getIcon } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ENGAGEMENT_DATA_TYPES, PushEngagementData, PushNotificationResponse } from 'components/Analytics/types';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import AnalyticsExperiencesAPI from 'services/api/analytics-experiences.api';
import { CLASSES, DATE_FORMAT_OPTIONS } from '../constants';
import useDebounce from 'hooks/useDebounce';
import { JourneyRulePayload } from 'interface/rule/rule.interface';
import { PushUniversalActionTypes } from 'components/ExperienceCanvas/constants';
import { epochToDateTimeString, intlNumberFormat } from 'helpers/common.helper';
import DownloadEngagementData from 'pages/Analytics/Overview/Engagement/DownloadEngagementData';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import useConfirmModal from 'hooks/useConfirmModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';
import { ReactComponent as IconDownloadData } from 'assets/icons/icon-download-data.svg';
import { ReactComponent as IconPushNotification } from 'assets/icons/push-notification.svg';
import { AdvancedDateRange, DateRange } from 'pages/Analytics/types';
import AnalyticsErrorState from 'pages/Analytics/components/AnalyticsErrorState/AnalyticsErrorState';

const tableHeaders = [
  {
    name: <>key</>,
    key: 'key',
    isVisible: false,
    isSortable: false,
  },
  {
    name: <span>Name</span>,
    key: 'name',
    isVisible: true,
    isSortable: false,
    tooltip: 'The name of the push notification, or its ID if no name is set.',
  },
  {
    name: <span>Associated context rule</span>,
    key: 'associated_context_rule',
    isVisible: true,
    isSortable: false,
    tooltip: 'The name of the context rule that evaluates users to send push notifications to.',
  },
  {
    name: <span>Status</span>,
    key: 'status',
    isVisible: true,
    isSortable: false,
    tooltip:
      'This is the status from the start of the experience. The possible statuses are: active, inactive, deleted.',
  },
  {
    name: (
      <span>
        Total /
        <br />
        Unique sent
      </span>
    ),
    key: 'unique_sent',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The total number of times this push notification was sent, and the number of unique users it was sent to, respectively.',
  },
  {
    name: (
      <span>
        Total /
        <br />
        Unique delivered
      </span>
    ),
    key: 'total_unique_delivered',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The total number of times this push notification was successfully delivered, and the number of unique users it was delivered to, respectively.',
  },
  {
    name: (
      <span>
        Total /
        <br />
        Unique bounces
      </span>
    ),
    key: 'unique_failed',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The total number of push notifications that failed to get delivered, and the number of unique users it was failed to get delivered, respectively',
  },
  {
    name: (
      <span>
        Total /
        <br />
        Unique opens
      </span>
    ),
    key: 'unique_open',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The total number of times this push notification was opened, and the number of unique users that opened it, respectively.',
  },
  {
    name: 'Unique open rate',
    key: 'unique_open_rate',
    isVisible: true,
    isSortable: true,
    tooltip: 'Unique opens divided by unique delivered.',
  },
];

const fetchPushNotifications = async (
  analyticsExperiencesApi: AnalyticsExperiencesAPI,
  page: number,
  perPage: number,
  dateRange: DateRange<Date>,
  search: string,
  sortBy: string,
  sortOrder: string,
): Promise<PushEngagementData> => {
  const res = await analyticsExperiencesApi.getPushNotificationsEngagement({
    lastFrom: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    lastTo: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
    offset: (page - 1) * perPage,
    limit: perPage,
    search,
    sortBy,
    sortOrder,
  });
  // we should catch the error, check status using isAxiosError and e.status,
  // then determining what to do from there, but for this first iteration it's fine
  const pushList: PushNotificationResponse[] = [];
  for (let i = 0; i < (res?.data?.Pushes?.length || 0); i++) {
    try {
      const pushRes = await analyticsExperiencesApi.getPushNotification(res.data.Pushes[i].pushId);
      pushList.push(pushRes);
    } catch (e) {}
  }

  const contextRules: JourneyRulePayload[] = [];
  for (let i = 0; i < (pushList.length || 0); i++) {
    const push = pushList[i];
    if (push) {
      for (let j = 0; j < push.rules.length; j++) {
        try {
          const ctxRuleRes = await analyticsExperiencesApi.getContextRule(push.rules[j].ruleID);
          contextRules.push(ctxRuleRes);
        } catch (e) {}
      }
    }
  }
  return {
    overview: res.data,
    pagination: res.pagination,
    pushes: pushList.length > 0 ? await Promise.all(pushList) : [],
    ctxRules: contextRules.length > 0 ? await Promise.all(contextRules) : [],
  };
};

const confirmationDialogProps: ConfirmationDialogProps = {
  icon: <IconDownloadData />,
  title: 'Download push table',
  description: 'Note that the table will be downloaded based on your current filters',
  primaryAction: {
    value: 'Download',
  },
  secondaryAction: {
    value: 'Cancel',
  },
};

const PushEngagementTable = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const analyticsExperiencesApi = useMemo(() => new AnalyticsExperiencesAPI(), []);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const [perPage, setPerPage] = useState(5);

  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState<AdvancedDateRange>([startOfToday, new Date(), 0, 'day']);
  const [queryDateRange, setQueryDateRange] = useState<AdvancedDateRange>([startOfToday, endOfToday]);
  const [search, setSearch] = useState('');
  const [sortByColumn, setSortByColumn] = useState('unique_sent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearch = useDebounce(search, 300);
  const [pushDataForCSV, setPushDataForCSV] = useState<Record<string, string>[]>([]);
  const [isAllPushDataLoading, setAllPushDataLoading] = useState(false);
  const [DownloadConfirmModal, showDownloadConfirmModal] = useConfirmModal(confirmationDialogProps);

  const { data, status, isFetching, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'push-notifications-engagement',
      currentPage,
      perPage,
      queryDateRange,
      debouncedSearch,
      sortByColumn,
      sortOrder,
    ],
    queryFn: () =>
      fetchPushNotifications(
        analyticsExperiencesApi,
        currentPage,
        perPage,
        [queryDateRange[0], queryDateRange[1]],
        debouncedSearch,
        sortByColumn,
        sortOrder,
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setCurrentPage(1);
    setSearch(value);
  };

  const handleHeaderSort = (e: { key: string }) => {
    if (e.key !== sortByColumn) {
      setSortByColumn(e.key);
      setSortOrder('desc');
    } else {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    }
  };

  const handleDateRangeChange = (newDate: AdvancedDateRange) => {
    setDateRange(newDate);
    const hasFullRange = newDate[0] !== null && newDate[1] !== null;

    if (hasFullRange) {
      const endOfDay = new Date(newDate[1] ?? new Date());
      if (newDate.length < 3 || (newDate.length >= 3 && newDate[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59, 999);
      }

      setQueryDateRange([newDate[0], endOfDay]);
    }
  };

  const generateCSVRecords = (data: PushEngagementData): Record<string, string>[] => {
    return data.overview.Pushes?.map((pushOverview) => {
      const push = data.pushes.find((p) => pushOverview.pushId === p.pushRequestID) || null;
      const ctxRules = data.ctxRules.filter((ctx) => push?.rules.some((rule) => rule.ruleID === ctx.id));
      const subname = [];
      subname.push(push ? (push.pushType === 1 ? 'Manual' : 'Automatic') : '----');
      const payloadData = push?.body[0].payload.data;
      if (payloadData?.contentId?.entity === 'content') subname.push('In-App Content');
      else if (payloadData?.contentId?.url) subname.push('Web Link');
      else if (payloadData?.actionScheme) {
        if (payloadData.actionScheme['en']) {
          const actionLink = PushUniversalActionTypes.find((actionType) =>
            payloadData?.actionScheme?.['en'].includes(actionType.prefix),
          );
          subname.push(`Action Link (${actionLink?.name})` || 'Action Link');
        } else subname.push('Action Link');
      } else if (push) subname.push('Basic');

      return {
        'Push ID': pushOverview.pushId,
        'Push Name': push?.name || pushOverview.pushId,
        'Push Sub Name': subname.join(', '),
        'Associated Context Rules':
          ctxRules
            .map((rule) => rule.name)
            .reduce(
              (prevRuleName, currentRuleName) => `${prevRuleName}${prevRuleName ? ', ' : ''} ${currentRuleName}`,
              '',
            ) || '',
        Status: push?.isActive ? 'Active' : 'Inactive',
        'Total Sent': (pushOverview?.totalSent || 0).toLocaleString(),
        'Unique Sent': (pushOverview?.uniqueSent || 0).toLocaleString(),
        'Total Delivered': (pushOverview?.totalDelivery || 0).toLocaleString(),
        'Unique Delivered': (pushOverview?.uniqueDelivery || 0).toLocaleString(),
        'Total Failed': (pushOverview?.totalFailed || 0).toLocaleString(),
        'Unique Failed': (pushOverview?.uniqueFailed || 0).toLocaleString(),
        'Total Open': (pushOverview?.totalOpen || 0).toLocaleString(),
        'Unique Open': (pushOverview?.uniqueOpen || 0).toLocaleString(),
        'Unique Open Rate': `${((pushOverview?.uniqueOpenRate || 0) * 100).toFixed(2)}%`,
      };
    });
  };

  const handleClickDownload = async () => {
    try {
      if (await showDownloadConfirmModal()) {
        setAllPushDataLoading(true);
        const allData = await queryClient.fetchQuery({
          queryKey: ['all-push-notifications-engagement', queryDateRange, debouncedSearch, sortByColumn, sortOrder],
          queryFn: () =>
            fetchPushNotifications(
              analyticsExperiencesApi,
              1,
              100000, // Sending higher limit to BE to fetch all records
              [queryDateRange[0], queryDateRange[1]],
              debouncedSearch,
              sortByColumn,
              sortOrder,
            ),
        });

        if (!allData.pushes?.length) {
          dispatch({
            type: 'SHOW_SNACKBAR',
            payload: {
              title: 'Info',
              content: 'No push engagement data found',
              type: 'info',
            },
          });
        } else {
          setPushDataForCSV(generateCSVRecords(allData));
        }
      }
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          title: 'Error',
          content: 'Something went wrong and could not download push engagement data.',
          type: 'error',
        },
      });
    } finally {
      setAllPushDataLoading(false);
    }
  };

  return (
    <div className={CLASSES.CARD}>
      <div className={CLASSES.CARD_HEADER}>
        <h2>
          <IconPushNotification className={CLASSES.HEADER_ICON} /> Push notifications
        </h2>
        <div className={CLASSES.DATE_RANGE}>
          {(dateRange[2] !== undefined || dateRange[3] === 'allTime') &&
            (dateRange[2] === 0
              ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
              : `${epochToDateTimeString(dateRange[0] || 0, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
                  dateRange[1] || 0,
                  'en-US',
                  DATE_FORMAT_OPTIONS,
                )}`)}
        </div>
        <div>
          <DateRangePicker
            className={CLASSES.DATE_RANGE_DD}
            value={dateRange}
            onChange={handleDateRangeChange}
            maxDate={new Date()}
            presets={DATERANGE_PRESETS}
            ariaLabel="Select Push engagement date range"
          />
        </div>
        <div className={CLASSES.DOWNLOAD}>
          <DownloadEngagementData
            data={pushDataForCSV}
            type={ENGAGEMENT_DATA_TYPES.PUSH}
            isDownloading={isAllPushDataLoading}
            onClickDownload={handleClickDownload}
          />
        </div>
      </div>
      {isLoading && !isError && (
        <div className={CLASSES.SPINNER}>
          <LoadingIcon />
        </div>
      )}
      {isError && <AnalyticsErrorState compact onRetry={() => refetch()} isRetrying={isFetching} />}
      {status === 'success' && (
        <>
          <div className={CLASSES.OVERVIEW}>
            <div className={CLASSES.OVERVIEW_ITEM}>
              <div className={CLASSES.OVERVIEW_ITEM_TITLE}>
                Total sent
                <FlightTooltip
                  className={CLASSES.OVERVIEW_ITEM_TITLE_TOOLTIP}
                  description="The total number of push notifications attempted to send."
                  direction="top"
                  maxWidth="240px"
                >
                  {getIcon('infoStroke', { width: '14px', height: '14px', fill: '#82899b' })}
                </FlightTooltip>
              </div>
              <p className={CLASSES.OVERVIEW_ITEM_DATA}>{data?.overview?.NotificationSent || 0}</p>
            </div>
            <div className={CLASSES.OVERVIEW_ITEM}>
              <div className={CLASSES.OVERVIEW_ITEM_TITLE}>
                Total delivered
                <FlightTooltip
                  className={CLASSES.OVERVIEW_ITEM_TITLE_TOOLTIP}
                  description="The number of push notifications successfully received by users' devices."
                  direction="top"
                  maxWidth="240px"
                >
                  {getIcon('infoStroke', { width: '14px', height: '14px', fill: '#82899b' })}
                </FlightTooltip>
              </div>
              <p className={CLASSES.OVERVIEW_ITEM_DATA}>{data?.overview?.NotificationDelivery || 0}</p>
            </div>
            <div className={CLASSES.OVERVIEW_ITEM}>
              <div className={CLASSES.OVERVIEW_ITEM_TITLE}>
                Total opened
                <FlightTooltip
                  className={CLASSES.OVERVIEW_ITEM_TITLE_TOOLTIP}
                  description="The number of notifications that users tapped to open."
                  direction="top"
                  maxWidth="240px"
                >
                  {getIcon('infoStroke', { width: '14px', height: '14px', fill: '#82899b' })}
                </FlightTooltip>
              </div>
              <p className={CLASSES.OVERVIEW_ITEM_DATA}>{data?.overview?.NotificationOpened || 0}</p>
            </div>
            <div className={CLASSES.OVERVIEW_ITEM}>
              <div className={CLASSES.OVERVIEW_ITEM_TITLE}>
                Open rate
                <FlightTooltip
                  className={CLASSES.OVERVIEW_ITEM_TITLE_TOOLTIP}
                  description="The percentage of delivered notifications that were opened. (Opened ÷ Delivered)"
                  direction="top"
                  maxWidth="240px"
                >
                  {getIcon('infoStroke', { width: '14px', height: '14px', fill: '#82899b' })}
                </FlightTooltip>
              </div>
              <p className={CLASSES.OVERVIEW_ITEM_DATA}>{(data?.overview?.NotificationOpenRate || 0)?.toFixed(2)}%</p>
            </div>
          </div>
          <div className={CLASSES.SEARCH_WRAPPER}>
            <FlightTextInput
              onChange={handleSearchChange}
              value={search}
              placeholderText="Search"
              iconInput="search"
              width="280px"
              hasClearIcon
            />
          </div>
          <div className={CLASSES.TABLE_WRAPPER}>
            <FlightTable
              tableHeaders={tableHeaders}
              tableData={data.overview.Pushes?.map((pushOverview) => {
                const push = data.pushes.find((p) => pushOverview.pushId === p.pushRequestID) || null;
                const ctxRules = data.ctxRules.filter((ctx) => push?.rules.some((rule) => rule.ruleID === ctx.id));
                const subname = [];
                subname.push(push ? (push.pushType === 1 ? 'Manual' : 'Automatic') : '----');
                const payloadData = push?.body[0].payload.data;
                if (payloadData?.contentId?.entity === 'content') subname.push('In-App Content');
                else if (payloadData?.contentId?.url) subname.push('Web Link');
                else if (payloadData?.actionScheme) {
                  if (payloadData.actionScheme['en']) {
                    const actionLink = PushUniversalActionTypes.find((actionType) =>
                      payloadData?.actionScheme?.['en'].includes(actionType.prefix),
                    );
                    subname.push(`Action Link (${actionLink?.name})` || 'Action Link');
                  } else subname.push('Action Link');
                } else if (push) subname.push('Basic');
                return {
                  key: pushOverview.pushId,
                  name: (
                    <>
                      <p>{push?.name || pushOverview.pushId}</p>
                      <p className={CLASSES.SUBNAME}>{subname.join(', ')}</p>
                    </>
                  ),
                  associated_context_rule: ctxRules.map((rule) => <p key={rule.id}>{rule.name}</p>),
                  status: push?.isActive ? (
                    <span className={`${CLASSES.PILL} ${CLASSES.ACTIVE_PILL}`}>Active</span>
                  ) : (
                    <span className={`${CLASSES.PILL} ${CLASSES.INACTIVE_PILL}`}>Inactive</span>
                  ),
                  unique_sent: (
                    <div className={CLASSES.TABLE_COMPLEX_NUMBER}>
                      <span title={intlNumberFormat(pushOverview?.totalSent || 0)}>
                        {intlNumberFormat(pushOverview?.totalSent || 0)}
                      </span>
                      /
                      <span title={intlNumberFormat(pushOverview?.uniqueSent || 0)}>
                        {intlNumberFormat(pushOverview?.uniqueSent || 0)}
                      </span>
                    </div>
                  ),
                  total_unique_delivered: (
                    <div className={CLASSES.TABLE_COMPLEX_NUMBER}>
                      <span title={intlNumberFormat(pushOverview?.totalDelivery || 0)}>
                        {intlNumberFormat(pushOverview?.totalDelivery || 0)}
                      </span>
                      /
                      <span title={intlNumberFormat(pushOverview?.uniqueDelivery || 0)}>
                        {intlNumberFormat(pushOverview?.uniqueDelivery || 0)}
                      </span>
                    </div>
                  ),
                  unique_failed: (
                    <div className={CLASSES.TABLE_COMPLEX_NUMBER}>
                      <span title={intlNumberFormat(pushOverview?.totalFailed || 0)}>
                        {intlNumberFormat(pushOverview?.totalFailed || 0)}
                      </span>
                      /
                      <span title={intlNumberFormat(pushOverview?.uniqueFailed || 0)}>
                        {intlNumberFormat(pushOverview?.uniqueFailed || 0)}
                      </span>
                    </div>
                  ),
                  unique_open: (
                    <div className={CLASSES.TABLE_COMPLEX_NUMBER}>
                      <span title={intlNumberFormat(pushOverview?.totalOpen || 0)}>
                        {intlNumberFormat(pushOverview?.totalOpen || 0)}
                      </span>
                      /
                      <span title={intlNumberFormat(pushOverview?.uniqueOpen || 0)}>
                        {intlNumberFormat(pushOverview?.uniqueOpen || 0)}
                      </span>
                    </div>
                  ),
                  unique_open_rate: `${((pushOverview?.uniqueOpenRate || 0) * 100).toFixed(2)}%`,
                };
              })}
              sortByKey={sortByColumn}
              sortOrder={sortOrder}
              handleHeaderSort={handleHeaderSort}
              hasPaginationBeforeTable={false}
              hasPaginationAfterTable={!!data?.pushes?.length}
              paginationProps={{
                totalPageNumber: Math.ceil(data.pagination.totalRecords / perPage) || 1,
                currentPageNumber: currentPage,
                rowsPerPageOptions: [5, 10, 15, 20],
                currentRowsPerPage: perPage,
                handlePageChange: (page: number) => setCurrentPage(page),
                handleRowsPerPageChange: (num: number | string) => setPerPage(Number(num)),
              }}
              isLoading={isFetching}
            />
            {!!!data?.overview?.Pushes?.length && !isFetching && (
              <div className={CLASSES.EMPTY_DATA}>
                {getIcon('search', {})}
                No results found
              </div>
            )}
          </div>
        </>
      )}
      <DownloadConfirmModal />
    </div>
  );
};

export default PushEngagementTable;
